import { CommonButton } from 'components/common/Forms/Button';
import Select, { GroupBase } from 'react-select';
import styled, { css, StyledComponent } from 'styled-components';
import { cssIf } from 'styles/partials/cssIf';
import { h3, h4, small, stripButton } from 'styles/Typography';
import { toRem, toRemStr } from 'utils/helpers';
import { LabelPositionType } from './Select';
import {
  FontBasedSize,
  getFontSize,
  getHeightBasedOnFontSize,
  getMaxMenuHeightBasedOnFontSize,
  getMinHeightBasedOnFontSize,
  heightBasedOnFontSize,
  LABEL_BEFORE,
} from '../../../styles/fontsStyleUtils';
import { FaCheck } from 'react-icons/fa';
import { ReactElement } from 'react';
import { isAndroid, isIOS, isSafari } from 'react-device-detect';
import { fonts } from '../../../styles/sizes';

export const LabeledWrapper = styled.label`
  color: var(--textsecondary);
  position: relative;
  /* margin-bottom: var(--s3); */
  width: 100%;
  display: flex;
  flex-direction: column;
`;
export const LegendLabel = styled.span`
  position: absolute;
  display: inline-block;
  color: var(--primary);
  font-size: var(--caption);
  padding: 0 var(--s1);
  background: var(--white);
  line-height: 1;
  top: calc(-1 * var(--s3) / 2);
  left: var(--s2);
  transition: 0.2s ease color;
`;
export const StyledTextArea = styled.textarea<
  {
    readonly hasError?: boolean;
  } & FontBasedSize
>`
  line-height: 1.3;
  resize: none;
  padding: var(--s1) var(--s2);
  ${getFontSize()}
  outline: none;
  color: inherit;
  width: 100%;
  border-radius: var(--s2);
  min-height: var(--s3);
  height: var(--s7);
  border: ${toRem(2)}rem solid var(--primary);
  background: var(--white);
  transition: 0.2s ease border-color;

  &::placeholder {
    color: var(--textdisabled);
  }

  &:hover,
  &:focus {
    border-color: var(--yellowaction);

    & + ${LegendLabel} {
      color: var(--textprimary);
    }
  }

  &:disabled {
    border-color: var(--textdisabled);
    cursor: not-allowed;

    &:hover,
    &:focus {
      border-color: var(--textdisabled);

      & + ${LegendLabel} {
        color: var(--textdisabled);
      }
    }

    & + ${LegendLabel} {
      color: var(--textdisabled);
    }
  }

  ${({ hasError }) =>
    hasError &&
    css`
      border-color: var(--rednegative);

      &:hover,
      &:focus {
        border-color: var(--rednegative);

        & + ${LegendLabel} {
          color: var(--rednegative);
        }
      }

      & + ${LegendLabel} {
        color: var(--rednegative);
      }
    `}
`;

export const BasicLabelStyle = styled.label`
  white-space: nowrap;
  padding: 0 var(--s1);
`;

export const ExpandingLabelStyle = styled(BasicLabelStyle)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 var(--s1);

  :hover {
    overflow: visible;
    z-index: 1;
    background: var(--white);
    padding-right: var(--s1);
  }
`;

export const UnderlineLinkStyle = styled.a`
  text-decoration: underline;
  white-space: nowrap;
  padding: 0 var(--s1);
  cursor: pointer;
  color: var(--textprimary);
`;

export type CommonInputLabelProps = {
  readonly fixedLabelWidth?: string;
  readonly labelBackgroundColor?: string;
};

export const commonInputLabelStyle = ({
  fixedLabelWidth,
  labelBackgroundColor,
}: CommonInputLabelProps) => css`
  position: absolute;
  padding: 0 var(--s1);
  top: calc(-1 * var(--s2));
  left: var(--s2);
  color: var(--primary);
  font-size: var(--caption);
  background-color: ${labelBackgroundColor || 'var(--white)'};
  line-height: 1;
  transition: 0.2s ease color;
  white-space: nowrap;
  z-index: 4;
  ${fixedLabelWidth &&
  css`
    min-width: ${fixedLabelWidth};
    text-align: right;
    margin-right: var(--s1);
  `}
`;

export const CommonInputLabel = styled.label<CommonInputLabelProps>`
  ${commonInputLabelStyle};
`;

type StyledSelectAdditionalProps = {
  readonly hasError?: boolean;
  readonly hasIcon?: boolean;
  readonly isDisabled?: boolean;
  readonly isMulti?: boolean;
  readonly dropdownMenuStyleOverride?: string;
} & FontBasedSize;

/**
 * Styled components doesn't forward generic props, so we need to
 * redefine the types.
 */
export const StyledSelect: <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: React.ComponentProps<typeof Select<Option, IsMulti, Group>> &
    StyledSelectAdditionalProps,
) => ReactElement = styled(Select)<StyledSelectAdditionalProps>`
  width: 100%;

  ${({ hasError, isDisabled }) =>
    hasError &&
    css`
      & + ${CommonInputLabel} {
        color: ${isDisabled ? 'var(--textdisabled)' : 'var(--rednegative)'};
      }
    `}
  &:hover {
    .common__control {
      border-color: ${({ hasError, isDisabled }) =>
        hasError
          ? 'var(--rednegative)'
          : isDisabled
          ? 'var(--textdisabled)'
          : 'var(--yellowaction)'};
      box-shadow: none;
    }

    & + ${CommonInputLabel} {
      color: ${({ hasError, isDisabled }) =>
        isDisabled
          ? 'var(--textdisabled)'
          : hasError
          ? 'var(--rednegative)'
          : 'var(--textprimary)'};
    }
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: not-allowed;

      & + ${CommonInputLabel} {
        color: var(--textdisabled);
      }
    `}
  .common__control {
    margin: 0;
    padding: 0;
    min-height: unset;
    box-shadow: none;
    border: ${toRem(2)}rem solid
      ${({ hasError, isDisabled }) =>
        isDisabled
          ? 'var(--textdisabled)'
          : hasError
          ? 'var(--rednegative)'
          : 'var(--primary)'};
    border-radius: var(--s2);
    ${heightBasedOnFontSize};
    align-items: baseline;
    cursor: pointer;
    z-index: 4;

    &:hover {
      border-color: ${({ hasError, isDisabled }) =>
        isDisabled
          ? 'var(--textdisabled)'
          : hasError
          ? 'var(--rednegative)'
          : 'var(--yellowaction)'};
      box-shadow: none;
    }

    &--is-disabled {
      background: var(--white);
    }
  }

  .common__dropdown-indicator {
    padding: 0 var(--s1) 0 0;
  }

  .common__value-container {
    flex-wrap: nowrap;
    align-items: center;
    width: 0 !important;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 100%;
    padding: ${({ hasIcon }) =>
      hasIcon ? '0 var(--s2) 0 var(--s4)' : '0 var(--s2)'};

    & > div {
      margin: 0;
    }

    & > div.common__multi-value {
      margin-right: var(--s1);
    }
  }

  .common__placeholder {
    color: var(--textdisabled);
    font-size: var(--body1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }

  .common__single-value {
    ${getFontSize()};
    color: var(--textsecondary);
  }

  .common__menu {
    margin: 0;
    border-radius: 0 0 var(--s2) var(--s2);
    box-shadow: var(--shadow-3);
    padding: 0;
    z-index: 5;

    &.canScrollDown {
      &::after {
        content: '';
        pointer-events: none;
        height: var(--s5);
        width: 100%;
        position: absolute;
        display: block;
        left: 0;
        z-index: 1;
        background: linear-gradient(
          180deg,
          var(--white) 0%,
          rgba(255, 255, 255, 0) 100%
        );
        bottom: 0;
        transform: rotate(180deg);
        border-radius: var(--s2) var(--s2) 0 0;
      }
    }

    & > div {
      padding: 0;
      ${getMaxMenuHeightBasedOnFontSize};
      ${({ dropdownMenuStyleOverride }) =>
        dropdownMenuStyleOverride ? dropdownMenuStyleOverride : ''};
    }

    .common__menu-list {
      padding-bottom: var(--s2);
    }
  }

  .common__option {
    padding: var(--s1) var(--s2);
    transition: 0.2s ease color, 0.2s ease background-color;
    cursor: pointer;
    ${getMinHeightBasedOnFontSize};
    /* display: flex; */
    line-height: 1;
    display: block;
    align-items: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    ${({ isMulti }) =>
      isMulti &&
      css`
        padding: var(--s1) var(--s2);

        &--is-selected {
          color: var(--white);

          label div {
            color: inherit;
          }
        }
      `}
    &--is-focused {
      background-color: var(--yellowmuted);
    }

    &--is-selected {
      background-color: var(--yellowaction);
    }
  }

  .common__indicators {
  }

  .common__clear-indicator {
    padding: 0;
  }
`;
export const FormGroup = styled.div``;
export const FormGroupTitle = styled.h4`
  ${h4};
  margin-bottom: var(--s3);
`;
export const FormColumn = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  & > div {
    flex: 1;
    margin-bottom: var(--s2);

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const FormRow = styled.div`
  display: flex;
  position: relative;
  flex-wrap: wrap;
  gap: var(--s3);

  & > div {
    flex: 1;
  }
`;

export const DialogFormRow = styled.div`
  display: flex;
  margin-bottom: var(--s3);

  & > div {
    flex: 1;
    margin-right: var(--s3);

    &:last-child {
      margin-right: 0;
    }
  }
`;

export const ButtonSetStyle = styled.div`
  justify-content: center;
  display: flex;
  row-gap: var(--s2);
  column-gap: var(--s3);
`;

export const FormActionsStyle = styled(FormRow)<{
  readonly align?: 'left' | 'center' | 'right';
  readonly buttonSize?: 'standard' | 'auto';
  readonly topMargin?: boolean;
}>`
  justify-content: ${({ align }) =>
    align === 'center'
      ? 'center'
      : align === 'left'
      ? 'flex-start'
      : 'flex-end'};

  flex-wrap: wrap;
  gap: var(--s2) var(--s3);

  ${cssIf('topMargin')`margin-top: var(--s3);`}
  & > * {
    ${({ buttonSize }) =>
      buttonSize === 'auto' ? '' : `min-width: ${toRem(120)}rem`};
  }
`;

export const FormError = styled.span`
  position: absolute;
  font-size: var(--caption);
  line-height: var(--s3);
  padding: 0 ${toRem(10)}rem;
  color: var(--rednegative);
  bottom: calc(-1 * var(--s3));
  left: 0;
`;

// Start Input UI
export type CommonInputIconProps = {
  readonly disabled?: boolean;
  readonly iconPosition?: 'left' | 'right';
};

export const commonInputIconStyle = ({
  disabled,
  iconPosition = 'left',
}: CommonInputIconProps) => css`
  position: absolute;
  ${iconPosition === 'right'
    ? css`
        right: ${toRem(12)}rem;
      `
    : css`
        left: ${toRem(12)}rem;
      `};
  top: 50%;
  transform: translateY(-50%);
  line-height: 0;
  font-size: var(--body1);
  color: ${disabled ? 'var(--textdisabled)' : 'var(--textsecondary)'};
`;

export const CommonInputIcon = styled.div<CommonInputIconProps>`
  ${commonInputIconStyle};
  pointer-events: none;
`;

const DEFAULT_INPUT_LABEL_COMPONENT = CommonInputLabel;

export const CommonInputLabelWrapper = styled.div<
  {
    readonly labelPosition: LabelPositionType;
    readonly disabled?: boolean;
    readonly labelComponent?: StyledComponent<any, any, any, any>;
  } & FontBasedSize
>`
  display: flex;
  flex-direction: row-reverse;
  position: relative;
  width: 100%;
  ${({ labelPosition, labelComponent = DEFAULT_INPUT_LABEL_COMPONENT }) =>
    labelPosition === LABEL_BEFORE &&
    css`
      ${labelComponent} {
        position: static;
        color: var(--textprimary);
        margin-top: var(--s2);
        max-height: ${toRem(24)}rem;
        ${getFontSize()}
      }
    `}
`;
export const InfoLabelTextValueStyle = styled.div<{
  addStyle?: string;
}>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 var(--s1);
  font-weight: 500;

  :hover {
    overflow: visible;
    z-index: 1;
    background: var(--white);
    padding-right: var(--s1);
  }
`;
type CommonInputWrapperProps = { flexWidth?: boolean; width?: string };
export const CommonInputWrapper = styled.div<
  CommonInputWrapperProps & FontBasedSize
>`
  display: flex;
  position: relative;
  font-size: ${({ fontBasedSize }) =>
    fontBasedSize ? `var(${fontBasedSize})` : `var(--body1)`};
  color: var(--textPrimary);
  margin-bottom: var(--s2);
  min-width: ${({ width }) => width || toRemStr(160)};
  ${cssIf('flexWidth')`
    min-width: auto;
    flex-shrink: 1;
    flex-grow: 1;
  `}
`;
export const CommonInputHelperWrapper = styled.div<{
  readonly noSpaceForHelperText?: boolean;
}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: ${({ noSpaceForHelperText }) =>
    noSpaceForHelperText ? 'unset' : `${toRem(56)}rem`};
`;

export const CommonInputHelperText = styled.span<{
  readonly hasError?: boolean;
  readonly disabled?: boolean;
}>`
  color: ${({ hasError, disabled }) =>
    disabled
      ? 'var(--textdisabled)'
      : hasError
      ? 'var(--rednegative)'
      : 'var(--textsecondary)'};
  font-size: var(--caption);
  padding: 0 ${toRem(10)}rem;
  line-height: var(--s3);
  width: 100%;
  pointer-events: none;
  transition: 0.2s ease color;
  margin-bottom: var(--s2);
  min-height: var(--s3);
`;

export type StyledCommonInputProps = {
  readonly hasError?: boolean;
  readonly hasIcon?: boolean;
  readonly iconPosition?: 'left' | 'right';
  readonly autocomplete?: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly labelComponent?: StyledComponent<any, any, any, any>;
} & FontBasedSize;

export const styledCommonInputStyle = ({
  hasError,
  hasIcon,
  iconPosition,
  fontBasedSize,
  labelComponent = DEFAULT_INPUT_LABEL_COMPONENT,
}: StyledCommonInputProps) => css`
  display: block;
  flex: 1;
  outline: none;
  background: var(--white);
  border: ${toRem(2)}rem solid var(--primary);
  border-radius: var(--s2);
  min-height: var(--s4);
  padding: 0 var(--s2);
  ${hasIcon
    ? iconPosition === 'right'
      ? css`
          padding-right: var(--s4);
        `
      : css`
          padding-left: var(--s4);
        `
    : ''};
  transition: 0.2s ease border-color;
  ${getFontSize()};
  ${fontBasedSize ? 'padding-bottom: 2px; padding-top:2px;' : ''}
  color: var(--textsecondary);
  width: 100%;

  &:hover,
  &:focus {
    border-color: var(--yellowaction);

    & ~ ${labelComponent} {
      color: var(--textprimary);
    }
  }

  &::placeholder {
    color: var(--textdisabled);
  }

  &:disabled,
  &[data-disabled='true'] {
    border-color: var(--textdisabled);
    cursor: not-allowed;

    &:hover,
    &:focus {
      border-color: var(--textdisabled);

      & ~ ${labelComponent} {
        color: var(--textdisabled);
      }
    }

    & ~ ${labelComponent} {
      color: var(--textdisabled);
    }
  }

  ${hasError &&
  css`
    border-color: var(--rednegative);

    &:hover,
    &:focus {
      border-color: var(--rednegative);

      & ~ ${labelComponent} {
        color: var(--rednegative);
      }
    }

    & ~ ${labelComponent} {
      color: var(--rednegative);
    }
  `}
  &[type="number"] {
    // hide native styles for number inputs
    -moz-appearance: textfield;

    &::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }
  }

  // polish styling for native date input per platform
  &[type='date'] {
    ${(isIOS || isSafari) &&
    `
      display: flex;
      &::-webkit-date-and-time-value {
        text-align: left !important;
      }
    `}
    ${isAndroid &&
    `
      padding: 0 var(--s1);
    `}
  }
`;

export const StyledCommonInput = styled.input<StyledCommonInputProps>`
  ${styledCommonInputStyle};
`;

export const CommonInputInner = styled.div<{
  readonly isDisabled?: boolean;
  iconRight?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  width: 100%;
  background-color: ${({ isDisabled }) =>
    isDisabled ? 'var(--textdisabled)' : 'var(--greentercial)'};
  border-radius: var(--s2);
  ${cssIf('iconRight')`
    ${CommonInputIcon} {
      left: auto;
      right: ${toRem(12)}rem;
    }
    ${StyledCommonInput} {
      padding: 0 var(--s4) 0 var(--s2);
    }
  `}
`;

export const TogglePassword = styled.button`
  ${stripButton};
  background-color: transparent;
  border-top-right-radius: var(--s2);
  border-bottom-right-radius: var(--s2);
  padding: 0 var(--s2);
  color: var(--greenprimary);
  height: var(--s4);
  display: flex;
  align-items: center;

  &:disabled {
    color: var(--textdisabled);
    cursor: not-allowed;
  }
`;
// End Input UI

export const DialogFormWrapper = styled.div`
  position: relative;
  padding-top: var(--s1);
`;
export const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--h3);
  margin-bottom: var(--s4);

  svg {
    line-height: 1;
    color: var(--greenprimary);
    margin-right: var(--s2);
  }
`;

const formTitleStyle = css`
  ${h3};
  text-transform: uppercase;
  font-weight: 500;
  line-height: 1;
`;

export const FormTitle = styled.h3`
  ${formTitleStyle}
`;
/**
 * Should be used only once on a page to represent the main title.
 * See https://nhsdigital.github.io/accessibility-checklist/pages/2.4.6-heading-labels-describe-topic/
 */
export const PageFormTitle = styled.h1`
  ${formTitleStyle}
`;
export const FormBody = styled.form``;
export const FormGridBody = styled.form`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  row-gap: calc(2 * var(--s3));
`;
export const CloseButton = styled(CommonButton)`
  position: absolute;
  top: 0;
  right: 0;
  font-size: var(--h3);
`;
/**
 * Visually hides an input element while keeping it accessible to screen readers.
 */
export const HiddenInput = styled.input`
  position: absolute; // Takes the element out of normal flow, so it doesn’t affect layout.
  width: 1px; // Gives it a minimal size—some screen readers ignore elements with no box dimensions.
  height: 1px; // The same as above.
  margin: 0; // Prevents stray spacing or outlines from affecting layout or causing a visible “dot.”
  padding: 0; // The same as above.
  border: 0; // The same as above.
  overflow: hidden; // Ensures that if anything (like focus outlines) appears, it won’t spill onto the page.
  clip: rect(
    0 0 0 0
  ); // The old, pre–CSS Clip Path way to cut the visible region to nothing. Needed for older browsers.
  clip-path: inset(
    50%
  ); // Modern, standards-based way to achieve the same thing. Used together for cross-browser safety.
  white-space: nowrap; // Prevents hidden text from wrapping and accidentally showing as 1-pixel-high line fragments in zoomed or high-contrast modes.
`;
export const CheckBoxesWrapper = styled.div<{
  readonly isVertical?: boolean;
}>`
  display: flex;
  margin-bottom: var(--s3);

  & > div {
    & + div {
      margin-left: var(--s3);
    }
  }

  ${({ isVertical }) =>
    isVertical &&
    css`
      flex-direction: column;

      & > div {
        & + div {
          margin-left: 0;
          margin-top: var(--s3);
        }
      }
    `}
`;
export const CheckLabel = styled.div<
  {
    readonly isRadio?: boolean;
    readonly labelPosition: 'before' | 'after';
  } & FontBasedSize
>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${getHeightBasedOnFontSize()};
  max-width: ${getHeightBasedOnFontSize()};
  min-height: ${getHeightBasedOnFontSize()};
  max-height: ${getHeightBasedOnFontSize()};
  border: ${toRem(1)}rem solid var(--textsecondary);
  border-radius: var(--s1);
  transition: 0.2s ease box-shadow, 0.2s ease border-color,
    0.2s ease background-color;
  background: var(--white);
  color: var(--textprimary);
  ${({ isRadio, labelPosition }) =>
    isRadio &&
    css`
      border-radius: 50%;
      position: relative;

      &::${labelPosition} {
        content: '';
        position: absolute;
        width: ${toRem(10)}rem;
        height: ${toRem(10)}rem;
        border-radius: 50%;
        background-color: var(--yellowaction);
        opacity: 0;
        transition: 0.2s ease opacity;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    `}
`;

export const CheckIcon = styled(FaCheck)``;

export const hoverAndFocusCheckLabelStyle = css`
  box-shadow: var(--shadow-3);
  border-color: var(--yellowaction);
`;

export const CheckboxWrapper = styled.label<
  {
    readonly isDisabled?: boolean;
    readonly onlyInRow?: boolean;
    readonly isToggle?: boolean;
    readonly inSelectList?: boolean;
    readonly labelPosition: 'before' | 'after';
  } & FontBasedSize
>`
  display: inline-flex;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  ${getFontSize('')};

  &:hover {
    ${CheckLabel} {
      ${hoverAndFocusCheckLabelStyle}
    }
  }

  ${HiddenInput}:focus + ${CheckLabel} {
    ${hoverAndFocusCheckLabelStyle}
  }

  ${HiddenInput}[type="checkbox"] {
    & + ${CheckLabel} ${CheckIcon} {
      display: none;
    }

    &:checked + ${CheckLabel} {
      background-color: var(--yellowaction);
      border-color: var(--yellowaction);

      ${CheckIcon} {
        display: block;
      }
    }

    &:disabled + ${CheckLabel} {
      background-color: ${({ isToggle }) =>
        isToggle ? 'var(--textdisabled)' : 'var(--white)'};
      border-color: var(--textdisabled);
    }

    &:disabled:checked + ${CheckLabel} {
      background-color: var(--textdisabled);
      border-color: var(--textdisabled);
    }
  }

  ${HiddenInput}[type="radio"] {
    &:checked + ${CheckLabel} {
      border-color: var(--yellowaction);

      &::${({ labelPosition }) => labelPosition} {
        opacity: 1;
      }
    }

    &:disabled + ${CheckLabel} {
      border-color: var(--textdisabled);

      &::${({ labelPosition }) => labelPosition} {
        background-color: var(--textdisabled);
      }
    }
  }

  ${({ isToggle, labelPosition }) =>
    isToggle &&
    css`
      ${CheckLabel} {
        min-width: ${toRem(24)}rem;
        max-width: ${toRem(24)}rem;
        min-height: ${toRem(8)}rem;
        max-height: ${toRem(8)}rem;
        margin-top: ${toRem(5)}rem;
        background-color: var(--textsecondary);
        border: none;
        position: relative;

        &::${labelPosition} {
          content: '';
          width: var(--s3);
          height: var(--s3);
          border-radius: 50%;
          left: 0;
          background-color: var(--white);
          border: ${toRem(1)}rem solid var(--textsecondary);
          position: absolute;
          top: 50%;
          transform: translate3d(0, -50%, 0);
          transition: 0.2s ease background-color, 0.2s ease border-color,
            0.2s ease transform;
        }
      }

      ${HiddenInput}[type="checkbox"] {
        &:checked + ${CheckLabel} {
          background-color: var(--rednegative);

          &::${labelPosition} {
            background-color: var(--yellowaction);
            border-color: var(--yellowaction);
            /* 6px here indicates to circle width - 2px borders (right + left) */
            transform: translate3d(${toRem(6)}rem, -50%, 0);
          }
        }
      }
    `}
  ${({ onlyInRow }) =>
    onlyInRow &&
    css`
      margin-bottom: var(--s4);
    `}
  ${({ inSelectList }) =>
    inSelectList &&
    css`
      pointer-events: none;
    `}
  align-items: center;
`;
const CheckboxName = styled.div`
  color: var(--textprimary);
  user-select: none;
  padding-top: ${toRem(1)}rem;
`;
export const CheckBoxNameAfter = styled(CheckboxName)`
  margin-left: var(--s2);
`;
export const CheckBoxNameBefore = styled(CheckboxName)`
  margin-right: var(--s2);
`;
export const DeleteAccountBtn = styled.button`
  ${stripButton};
  color: var(--rednegative);
  display: block;
  margin-bottom: var(--s3);
`;
export const InputRangeWrapper = styled.div<{
  showMaxTopRight: boolean;
}>`
  display: flex;
  justify-content: flex-end;
  padding: 0 var(--s1);
  ${({ showMaxTopRight }) =>
    showMaxTopRight
      ? css`
          align-items: flex-end;
          min-height: ${toRem(50)}rem;
          padding-bottom: ${toRem(1)}rem;
        `
      : css`
          align-items: center;
          min-height: ${toRem(25)}rem;
        `}
`;

const rangeZIndex = 1;

export const InputRangePrefixWithSliderStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: ${rangeZIndex};
`;

export const InputRangeTopRight = styled.div`
  position: absolute;
  top: 0;
  padding: ${toRemStr(5)};
  color: var(--textprimary);
  background-color: var(--disabled);
  height: ${toRemStr(35)};
  font-size: ${toRemStr(20)};
`;

export const InputRangeTrackHolder = styled.div`
  width: 100%;
  min-width: ${toRem(200)}rem;
  max-width: ${toRem(200)}rem;
  position: relative;

  & > div {
    height: var(--s2);
  }
`;

export const InputRangeTrackTrack = styled.div`
  width: 100%;
  border-radius: 10px;
  background-color: var(--disabled);
  position: absolute;
`;

export const InputRangeThumb = styled.div<{
  readonly color: string;
  readonly textColor: string;
}>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  display: flex;
  align-items: baseline;
  justify-content: center;
  border: white 1px solid;
  padding-top: 1.5px;
  box-sizing: border-box;
  font-size: ${toRem(16)}rem;
  font-weight: 500;
  color: ${({ textColor }) => textColor};
  outline: none;
  transition: 0.2s ease transform;
`;

export const InputRangePrefix = styled.h4<{
  readonly prefixType?: 'small' | 'big';
}>`
  ${({ prefixType }) => (prefixType === 'small' ? small : h4)};
  text-transform: uppercase;
  margin-right: var(--s2);
  font-weight: 700;
  text-align: right;
`;

export const CheckboxInputStyled = styled.div`
  display: flex;
  align-content: center;
`;

export const Required = styled.span`
  color: var(--rednegative);
  font-size: ${toRem(fonts.caption / 2)}rem;
  vertical-align: top;
`;
